import React from 'react';
import { X, CheckCircle2, Calendar, Activity, Database } from 'lucide-react';

interface IntegrationDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    integration: {
        name: string;
        logo: React.ReactNode;
        description: string;
        status: 'active' | 'connected' | 'disconnected';
        dataPoints: string[];
        lastSync?: string;
    } | null;
}

export const IntegrationDetailModal: React.FC<IntegrationDetailModalProps> = ({ isOpen, onClose, integration }) => {
    if (!isOpen || !integration) return null;

    // Audit D-04: el estado refleja el dato real, no un "siempre activa".
    const isLive =
        integration.status === 'active' || integration.status === 'connected';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity"
                onClick={onClose}
            ></div>

            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Detalle de la integración ${integration.name}`}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto transform rounded-2xl bg-white shadow-2xl transition-all"
            >
                {/* Header */}
                <div className="relative h-32 bg-gradient-to-r from-violet-600 to-indigo-600">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                    >
                        <X size={20} />
                    </button>

                    <div className="absolute -bottom-10 left-8 flex items-end">
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white p-2 shadow-xl border-4 border-white">
                            {integration.logo}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="px-8 pt-14 pb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-bold text-gray-900">{integration.name}</h2>

                        {isLive ? (
                            <div className="flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 border border-green-200">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                </span>
                                Conexión activa
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-500 border border-slate-200">
                                <span className="h-2.5 w-2.5 rounded-full bg-slate-400"></span>
                                Desconectada
                            </div>
                        )}
                    </div>

                    <p className="text-gray-600 text-lg leading-relaxed mb-8">
                        {integration.description}
                    </p>

                    <div className="flex flex-col gap-8">
                        {/* Stats / Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                <Activity size={16} />
                                Estado del Sistema
                            </h3>
                            <div className="rounded-xl bg-gray-50 p-4 border border-gray-100 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Última sincronización</span>
                                    <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                        <Calendar size={14} className="text-gray-400" />
                                        {integration.lastSync || "Sin datos de sincronización"}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Estado de conexión</span>
                                    <span className={`text-sm font-medium ${isLive ? "text-green-600" : "text-slate-500"}`}>
                                        {isLive ? "Estable" : "Sin conexión"}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Permisos</span>
                                    <span className="text-sm font-medium text-gray-900">Lectura / Escritura</span>
                                </div>
                            </div>
                        </div>

                        {/* Active Features */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                <Database size={16} />
                                Datos Sincronizados
                            </h3>
                            <div className="space-y-2">
                                {integration.dataPoints.map((point, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-white shadow-sm hover:border-violet-100 hover:shadow-md transition-all">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                                            <CheckCircle2 size={16} />
                                        </div>
                                        <span className="font-medium text-gray-700">{point}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    );
};
